// The rules of the site's hints (the small cards that point at a feature once), and the memory of them in the browser.
//
// The rules are those the large sites settled on after years of testing how often a tip may come back before it turns into noise:
//  - a hint is shown at most twice, and never again once the person has used the feature it is about (Google Workspace, Duolingo);
//  - "Got it" ends it for good; the cross closes it for 14 days (Gmail, LinkedIn), and a second cross ends it for good;
//  - no more than one hint on a page, and two in a day, across all hints (LinkedIn, Notion: a tip is a guest, not a campaign);
//  - never at once: 8 seconds after the page has loaded and after the person has done something (a scroll, a click, a key), so that it
//    never interrupts the first look; never over a window, a menu or the cookie banner; never while someone is typing;
//  - it goes by itself after 12 seconds, takes no focus, and answers Escape;
//  - what is kept is the id of the hint, a count and a few dates, nothing about the person; records older than 180 days are forgotten
//    (a feature seen half a year ago is worth a reminder), and no more than 50 are kept;
//  - without storage (a private window, a blocked store) the memory lasts for the visit only;
//  - the person can start over: "Show the tips again" in the footer.
// This file is the rules and the memory, with no page in it, so that every rule has a test.

export const HINT_RULES = {
  maxShows: 2,
  snoozeDays: 14,
  maxSnoozes: 2,
  maxPerDay: 2,
  maxPerPage: 1,
  minDwellMs: 8_000,
  autoHideMs: 12_000,
  countAsShownAfterMs: 1_500,
  forgetAfterDays: 180,
  maxRecords: 50,
  storageKey: 'rec:hints:v1',
} as const;

const DAY = 86_400_000;

export interface HintRecord {
  /** Times it was on the screen long enough to be read. */
  shown: number;
  lastShown: number;
  /** "Got it" or the feature used: never again. */
  done?: number;
  /** The cross: not before this time. */
  snoozeUntil?: number;
  snoozes?: number;
  /** The version of the hint's text when it was last seen; a new version starts over. */
  version: number;
}

export interface HintState {
  v: 1;
  records: Record<string, HintRecord>;
  /** Times a hint was shown, by day (YYYY-MM-DD, local). */
  day: { date: string; count: number };
  firstSeen: number;
}

export interface HintDef {
  id: string;
  /** Raise it when the text changes in a way that is worth saying again. */
  version?: number;
  /** Higher goes first when several are eligible. */
  priority?: number;
  /** Where it applies: tested on the path without the language prefix. */
  path: RegExp;
  /** The element it points at carries data-hint="<id>". */
  en: string;
  fr: string;
  /** Other conditions the page knows (a filter was used, a list is not empty). */
  when?: (ctx: HintContext) => boolean;
}

export interface HintContext {
  now: number;
  /** The path without the language prefix, e.g. "/qc/montreal/some-listing". */
  path: string;
  /** Hints already shown on this page view. */
  shownThisPage: number;
  /** Something that must not be covered is open: a window, a menu, the cookie banner. */
  blocked: boolean;
  /** Ids of the hints whose element is on the page and visible. */
  anchors: ReadonlySet<string>;
  extra?: Record<string, boolean>;
}

const todayKey = (now: number) => {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const emptyState = (now: number): HintState => ({ v: 1, records: {}, day: { date: todayKey(now), count: 0 }, firstSeen: now });

/** The state from what was stored, or a fresh one: anything unreadable is simply forgotten. Old records are dropped here. */
export function readState(raw: string | null, now: number): HintState {
  if (!raw) return emptyState(now);
  try {
    const parsed = JSON.parse(raw) as Partial<HintState>;
    if (!parsed || parsed.v !== 1 || typeof parsed.records !== 'object' || parsed.records === null) return emptyState(now);
    const records: Record<string, HintRecord> = {};
    for (const [id, r] of Object.entries(parsed.records)) {
      if (!r || typeof r !== 'object' || typeof r.shown !== 'number' || typeof r.lastShown !== 'number') continue;
      if (now - Math.max(r.lastShown, r.done ?? 0) > HINT_RULES.forgetAfterDays * DAY) continue;
      records[id] = { shown: r.shown, lastShown: r.lastShown, version: typeof r.version === 'number' ? r.version : 1, done: r.done, snoozeUntil: r.snoozeUntil, snoozes: r.snoozes };
    }
    const day = parsed.day && typeof parsed.day.date === 'string' && typeof parsed.day.count === 'number' ? parsed.day : { date: todayKey(now), count: 0 };
    return { v: 1, records: trim(records), day, firstSeen: typeof parsed.firstSeen === 'number' ? parsed.firstSeen : now };
  } catch {
    return emptyState(now);
  }
}

/** The newest records, no more than the cap. */
function trim(records: Record<string, HintRecord>): Record<string, HintRecord> {
  const entries = Object.entries(records);
  if (entries.length <= HINT_RULES.maxRecords) return records;
  entries.sort((a, b) => Math.max(b[1].lastShown, b[1].done ?? 0) - Math.max(a[1].lastShown, a[1].done ?? 0));
  return Object.fromEntries(entries.slice(0, HINT_RULES.maxRecords));
}

export const writeState = (state: HintState): string => JSON.stringify({ ...state, records: trim(state.records) });

/** Why a hint may not be shown now, or null when it may. */
export function refusal(def: HintDef, state: HintState, ctx: HintContext): string | null {
  const rec = state.records[def.id];
  const version = def.version ?? 1;
  const same = rec && rec.version === version ? rec : undefined;
  if (same?.done) return 'done';
  if (same && same.shown >= HINT_RULES.maxShows) return 'enough';
  if (same?.snoozeUntil && same.snoozeUntil > ctx.now) return 'snoozed';
  if (ctx.blocked) return 'blocked';
  if (ctx.shownThisPage >= HINT_RULES.maxPerPage) return 'page-full';
  const today = state.day.date === todayKey(ctx.now) ? state.day.count : 0;
  if (today >= HINT_RULES.maxPerDay) return 'day-full';
  if (!def.path.test(ctx.path)) return 'elsewhere';
  if (!ctx.anchors.has(def.id)) return 'no-anchor';
  if (def.when && !def.when(ctx)) return 'condition';
  return null;
}

/** The hint to show now: the eligible one of the highest priority, or null. */
export function pickHint(defs: readonly HintDef[], state: HintState, ctx: HintContext): HintDef | null {
  const ok = defs.filter((d) => refusal(d, state, ctx) === null);
  ok.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return ok[0] ?? null;
}

const fresh = (state: HintState, def: HintDef, now: number): HintRecord => {
  const version = def.version ?? 1;
  const old = state.records[def.id];
  return old && old.version === version ? { ...old } : { shown: 0, lastShown: now, version };
};

/** The hint was on the screen long enough to be read. */
export function recordShown(state: HintState, def: HintDef, now: number): HintState {
  const rec = fresh(state, def, now);
  rec.shown += 1;
  rec.lastShown = now;
  const date = todayKey(now);
  const count = state.day.date === date ? state.day.count + 1 : 1;
  return { ...state, records: trim({ ...state.records, [def.id]: rec }), day: { date, count } };
}

/** "Got it", or the feature was used: never again. */
export function recordDone(state: HintState, def: Pick<HintDef, 'id' | 'version'>, now: number): HintState {
  const rec = fresh(state, def as HintDef, now);
  rec.done = now;
  rec.lastShown = rec.lastShown || now;
  return { ...state, records: trim({ ...state.records, [def.id]: rec }) };
}

/** The cross: out of the way for two weeks, and for good the second time. */
export function recordSnoozed(state: HintState, def: HintDef, now: number): HintState {
  const rec = fresh(state, def, now);
  rec.snoozes = (rec.snoozes ?? 0) + 1;
  if (rec.snoozes >= HINT_RULES.maxSnoozes) rec.done = now;
  else rec.snoozeUntil = now + HINT_RULES.snoozeDays * DAY;
  return { ...state, records: trim({ ...state.records, [def.id]: rec }) };
}

/** "Show the tips again". */
export const resetState = (now: number): HintState => emptyState(now);
